import { useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import type { ChartOptions } from "chart.js";
import { Line } from "react-chartjs-2";
import type { EnrollmentMonthlyConversionResponse } from "@t3tools/contracts";
import { Button } from "~/components/ui/button";
import { EnrollmentClinicMultiSelect } from "./enrollmentClinicMultiSelect";
import { ChartPanel } from "./dashboardKit";
import { cohortStyle } from "./enrollmentCohortCurves";
import {
  monthlyConversionDataset,
  monthlyLabel,
  type MonthlyConversionKind,
} from "./enrollmentMonthlyConversion";

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend);
const DEFAULT_CLINICS = new Set([
  "Memory Treatment Centers",
  "Lone Star Neurology",
  "The Neurology Center",
]);

function ConversionSection(props: {
  data: EnrollmentMonthlyConversionResponse;
  kind: MonthlyConversionKind;
  title: string;
}) {
  const [selection, setSelection] = useState<string[] | null>(null);
  const defaults = props.data.clinics.filter((clinic) => DEFAULT_CLINICS.has(clinic.name));
  const selectedIds =
    selection ??
    (defaults.length ? defaults : props.data.clinics.slice(0, 3)).map((clinic) => clinic.id);
  const selected = props.data.clinics.filter((clinic) => selectedIds.includes(clinic.id));
  const months = props.data.clinics[0]?.points ?? [];
  const hasResults = selected.some((clinic) =>
    clinic.points.some((point) => point.measured && point[props.kind].denominator > 0),
  );
  const pending = months.filter((point) => !point.measured);
  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: { mode: "index", intersect: false },
    scales: {
      x: { type: "category", title: { display: true, text: "Cohort month" } },
      y: {
        min: 0,
        max: 100,
        title: { display: true, text: "Conversion (%)" },
        ticks: { callback: (value) => value + "%" },
      },
    },
    plugins: {
      legend: { position: "bottom", display: selected.length <= 8 },
      tooltip: {
        callbacks: {
          label: (item) => {
            const clinic = selected[item.datasetIndex];
            const point = clinic?.points[item.dataIndex];
            const result = point?.[props.kind];
            return clinic && result && result.numerator !== null
              ? clinic.name +
                  ": " +
                  result.numerator +
                  "/" +
                  result.denominator +
                  " converted (" +
                  (item.parsed.y ?? 0).toFixed(1) +
                  "%)"
              : "";
          },
          afterLabel: (item) => {
            const point = selected[item.datasetIndex]?.points[item.dataIndex];
            return point
              ? "Measured through " +
                  point.measurementDate +
                  (point[props.kind].denominator < 20 ? " · Small cohort" : "")
              : "";
          },
        },
      },
    },
  };
  const undated = selected.reduce((sum, clinic) => sum + clinic.undatedReferrals, 0);
  return (
    <section aria-label={props.title} className="min-w-0">
      <ChartPanel
        title={props.title}
        action={
          <EnrollmentClinicMultiSelect
            clinics={props.data.clinics}
            selected={selectedIds}
            onChange={setSelection}
            label={props.title + " clinics"}
          />
        }
      >
        <p className="mb-4 text-sm text-muted-foreground">
          Monthly cohorts, measured 15 days after month end. Hollow points have fewer than 20
          eligible Carespaces.
        </p>
        {hasResults ? (
          <div className="h-[360px] min-w-0">
            <Line
              role="img"
              aria-label={props.title + " monthly chart"}
              data={{
                labels: months.map((point) => monthlyLabel(point.month)),
                datasets: selected.map((clinic) =>
                  monthlyConversionDataset(clinic, props.kind, props.data.clinics.indexOf(clinic)),
                ),
              }}
              options={options}
            />
          </div>
        ) : (
          <p role="status" className="py-16 text-center text-sm text-muted-foreground">
            {selected.length
              ? "No measured cohorts for the selected clinics."
              : "Select clinics to display monthly conversion."}
          </p>
        )}
        {hasResults && selected.length > 8 ? (
          <ul
            aria-label="Clinics shown"
            className="mt-3 flex max-h-32 flex-wrap gap-x-4 gap-y-2 overflow-y-auto text-xs"
          >
            {selected.map((clinic) => {
              const style = cohortStyle(props.data.clinics.indexOf(clinic));
              return (
                <li key={clinic.id} className="flex items-center gap-2">
                  <svg width="24" height="10" aria-hidden="true">
                    <line
                      x1="0"
                      x2="24"
                      y1="5"
                      y2="5"
                      stroke={style.color}
                      strokeWidth="2"
                      strokeDasharray={style.dash.join(" ")}
                    />
                  </svg>
                  {clinic.name}
                </li>
              );
            })}
          </ul>
        ) : null}
        {pending.map((point) => (
          <p key={point.month} className="mt-3 text-xs text-muted-foreground">
            {monthlyLabel(point.month, true)} awaits measurement through {point.measurementDate};
            its percentage is pending.
          </p>
        ))}
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer font-medium">View cohort counts</summary>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr>
                  <th className="p-2">Clinic</th>
                  {months.map((point) => (
                    <th key={point.month} className="p-2">
                      <span className="block whitespace-nowrap">{monthlyLabel(point.month)}</span>
                      <span className="whitespace-nowrap font-normal text-muted-foreground">
                        Through {point.measurementDate}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {selected.map((clinic) => (
                  <tr key={clinic.id} className="border-t border-border">
                    <th className="whitespace-nowrap p-2 font-medium">{clinic.name}</th>
                    {clinic.points.map((point) => {
                      const result = point[props.kind];
                      return (
                        <td key={point.month} className="p-2">
                          {result.numerator === null ? (
                            "Pending"
                          ) : result.denominator === 0 ? (
                            "N/A (0 eligible)"
                          ) : (
                            <>
                              <span className="block">
                                {result.numerator}/{result.denominator}
                              </span>
                              <span className="text-muted-foreground">
                                {((100 * result.numerator) / result.denominator).toFixed(1)}%
                              </span>
                            </>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
        <details className="mt-4 text-sm">
          <summary className="cursor-pointer font-medium">
            How this conversion is calculated
          </summary>
          <div className="mt-3 space-y-2 text-muted-foreground">
            <p>
              Currently archived Carespaces and those currently ineligible for GUIDE are excluded
              from both counts. Each Carespace counts once per month; every converted Carespace must
              belong to that month’s denominator.
            </p>
            {props.kind === "referral" ? (
              <>
                <p>
                  The cohort uses the earliest matched ESPO profile date at the same clinic,
                  otherwise the Carespace creation date. Confirmed links, MBI, MRN plus birth date,
                  and name plus birth date identify profiles. Foreign-clinic-only matches have no
                  valid referral date for this clinic.
                </p>
                {undated ? (
                  <p>
                    {undated.toLocaleString("en-US")} eligible Carespaces in the selected clinics
                    have no reliable referral date and are omitted.
                  </p>
                ) : null}
              </>
            ) : (
              <>
                <p>
                  The cohort uses the earliest clinical_visit in the month, including records
                  imported later. All visit statuses and provider roles are included. Carespaces
                  already Submitted, Awaiting additional referral information, or GUIDE-aligned
                  before that visit are excluded from both counts.
                </p>
                <p>
                  Visit times use the clinic’s timezone. When the visit time is missing, an
                  earlier-day conversion is excluded; same-day ordering remains unknown. Historical
                  visit coverage may be incomplete.
                </p>
              </>
            )}
            <p>
              Conversion means reaching Submitted or Awaiting additional referral information by the
              measurement point, through the 15th day after month end in UTC. The earlier of a
              status’s recorded and effective dates reconstructs its timing from histories available
              today. Months awaiting measurement have no conversion percentage.
            </p>
          </div>
        </details>
      </ChartPanel>
    </section>
  );
}

export function EnrollmentMonthlyConversionSections(props: {
  data: EnrollmentMonthlyConversionResponse | undefined;
  loading: boolean;
  error: string;
  onRetry: () => void;
}) {
  if (props.error)
    return (
      <div role="alert" className="rounded-lg border border-border p-4">
        <p className="mb-3 text-sm">{props.error}</p>
        <Button variant="outline" onClick={props.onRetry}>
          Retry monthly conversion
        </Button>
      </div>
    );
  if (props.loading)
    return (
      <p role="status" className="py-8 text-sm text-muted-foreground">
        Loading monthly conversion…
      </p>
    );
  if (!props.data) return null;
  return (
    <div className="mt-6 flex flex-col gap-6">
      <ConversionSection
        data={props.data}
        kind="referral"
        title="Post Carespace creation conversion"
      />
      <ConversionSection
        data={props.data}
        kind="postVisit"
        title="Post provider appointment conversion"
      />
    </div>
  );
}
